// src/pages/EditEmployee.jsx - COMPLETE FIXED VERSION

import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useNavigate, useLocation, useParams } from 'react-router-dom'; // 👈 Added useParams
import { useAppContext } from '../context/AppContext';
import {
  FiArrowLeft, FiSave, FiUpload, FiFile, FiTrash2,
  FiUser, FiUsers, FiHome, FiBriefcase, FiDollarSign,
  FiEye, FiCheck
} from 'react-icons/fi';

// ─── ALL sub-components ──────────────────────────────────────────────
const InputField = ({ label, name, type = 'text', required, placeholder, readOnly, value, error, onChange }) => (
  <div className="form-group">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value || ''}
      onChange={readOnly ? undefined : onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      className={`w-full px-4 py-2.5 border rounded-xl transition
        ${error ? 'border-red-500' : 'border-gray-200'}
        ${readOnly
          ? 'bg-gray-50 text-gray-500 cursor-not-allowed'
          : 'focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100'
        }`}
    />
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
);

const SelectField = ({ label, name, options, required, value, onChange }) => (
  <div className="form-group">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      name={name}
      value={value || ''}
      onChange={onChange}
      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 bg-white"
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

const TextAreaField = ({ label, name, rows = 3, placeholder, value, onChange }) => (
  <div className="form-group">
    <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
    <textarea
      name={name}
      rows={rows}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none"
    />
  </div>
);

const FileUploadField = ({ label, fieldName, accept = '.pdf,.jpg,.jpeg,.png', value, onUpload, onRemove }) => {
  const isExistingUrl = value && typeof value === 'string' && value.startsWith('http');
  const isNewFile = value instanceof File;
  const filename = isExistingUrl
    ? value.split('/').pop()
    : isNewFile ? value.name : null;
  const isImage = filename && /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);

  return (
    <div className="file-upload-field">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>

      {isExistingUrl && (
        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FiCheck className="text-green-600" size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-700 truncate">{filename}</p>

            </div>
          </div>
          <div className="flex gap-1 ml-2 flex-shrink-0">
            <a
              href={value}
              target="_blank"
              rel="noreferrer"
              className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
              title="View"
            >
              <FiEye size={16} />
            </a>
            <button
              type="button"
              onClick={() => onRemove(fieldName)}
              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition"
              title="Remove (allows uploading new file)"
            >
              <FiTrash2 size={16} />
            </button>
          </div>
        </div>
      )}

      {isNewFile && (
        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-3 min-w-0">
            {isImage
              ? <img src={URL.createObjectURL(value)} alt="preview" className="w-10 h-10 object-cover rounded" />
              : <FiFile className="text-indigo-500 text-2xl flex-shrink-0" />
            }
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-700 truncate">{value.name}</p>
              <p className="text-xs text-blue-600">New file — will be uploaded</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onRemove(fieldName)}
            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition ml-2 flex-shrink-0"
            title="Remove"
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      )}

      {!value && (
        <label className="cursor-pointer block">
          <input
            type="file"
            accept={accept}
            onChange={(e) => onUpload(e, fieldName)}
            className="hidden"
          />
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-500 transition cursor-pointer">
            <FiUpload className="mx-auto text-gray-400 text-2xl mb-2" />
            <p className="text-sm text-gray-500">Click to upload or drag and drop</p>
            <p className="text-xs text-gray-400 mt-1">PNG, JPG, PDF (Max 5MB)</p>
          </div>
        </label>
      )}
    </div>
  );
};

const ALL_DOC_FIELDS = [
  'aadharCard', 'panCard', 'passportPhoto', 'bankPassbook', 'resume',
  'offerLetter', 'appointmentLetter', 'previousSalarySlip',
  'previousExperienceCertificate', 'currentExperienceLetter',
  'tenthMarksheet', 'twelfthMarksheet', 'graduationMarksheet', 'postGraduationMarksheet',
];

// ─── Main Component ────────────────────────────────────────────────────
const EditEmployee = () => {
  const { id } = useParams(); // 👈 Get ID from URL
  const navigate = useNavigate();
  const { refreshData } = useAppContext();
  const location = useLocation(); // kept only for name preview while loading, not as data source
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true); // 👈 Loading state for fetch
  const [activeTab, setActiveTab] = useState('basic');
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    id: '', employeeId: '', name: '', email: '', phone: '',
    dateOfBirth: '', gender: 'Male', bloodGroup: '', maritalStatus: 'Single',
    nationality: 'Indian', fatherName: '', fatherContact: '', motherName: '',
    motherContact: '', spouseName: '', spouseContact: '',
    address: '', permanentAddress: '', emergencyContactName: '', emergencyContactRelation: '',
    emergencyContactNumber: '', department: '', position: '',
    workLocation: '', employmentType: 'Full-time', joiningDate: '',
    status: 'Active', basicSalary: '', currentSalary: '',
    previousCompany: '', previousSalary: '', bankName: '', accountNumber: '',
    ifscCode: '', weekly_off_days: ['Sunday'],
    aadharCard: null, panCard: null, passportPhoto: null, bankPassbook: null,
    resume: null, offerLetter: null, appointmentLetter: null,
    previousSalarySlip: null, previousExperienceCertificate: null,
    currentExperienceLetter: null, tenthMarksheet: null, twelfthMarksheet: null,
    graduationMarksheet: null, postGraduationMarksheet: null,
  });

  // In src/pages/EditEmployee.jsx - REPLACE your useEffect with this:

  useEffect(() => {
    const loadEmployeeData = async () => {
      setFetchingData(true);
      try {
        let employeeData = null;

        // ✅ Force fetch from backend using the URL ID
        if (id) {
          const raw = await api.get(`/api/employees/${id}/`);
          employeeData = raw && typeof raw === 'object' && 'data' in raw &&
            raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)
            ? raw.data   // raw axios response object — unwrap it
            : raw;       // already unwrapped by an interceptor — use as-is

        }

        if (!employeeData) {
          throw new Error('Employee data not found');
        }

        const getValue = (value, defaultValue = '') => {
          if (value === null || value === undefined) return defaultValue;
          return value;
        };

        // ✅ COMPLETE MAPPING
        const mapped = {
          id: getValue(employeeData.id || employeeData.pk, ''),
          employeeId: getValue(employeeData.employeeId || employeeData.employee_id, ''),
          name: getValue(employeeData.name || employeeData.full_name || employeeData.fullName, ''),
          email: getValue(employeeData.email || employeeData.user?.email, ''),
          phone: getValue(employeeData.phone || employeeData.user?.phone, ''),
          dateOfBirth: getValue(employeeData.dateOfBirth || employeeData.date_of_birth || employeeData.user?.date_of_birth, ''),
          gender: getValue(employeeData.gender, 'Male'),
          bloodGroup: getValue(employeeData.bloodGroup || employeeData.blood_group, ''),
          maritalStatus: getValue(employeeData.maritalStatus || employeeData.marital_status, 'Single'),
          nationality: getValue(employeeData.nationality, 'Indian'),

          fatherName: getValue(employeeData.fatherName || employeeData.father_name, ''),
          fatherContact: getValue(employeeData.fatherContact || employeeData.father_contact, ''),
          motherName: getValue(employeeData.motherName || employeeData.mother_name, ''),
          motherContact: getValue(employeeData.motherContact || employeeData.mother_contact, ''),
          spouseName: getValue(employeeData.spouseName || employeeData.spouse_name, ''),
          spouseContact: getValue(employeeData.spouseContact || employeeData.spouse_contact, ''),

          address: getValue(employeeData.address || employeeData.currentAddress || employeeData.current_address, ''),
          permanentAddress: getValue(employeeData.permanentAddress || employeeData.permanent_address, ''),
          emergencyContactName: getValue(employeeData.emergencyContactName || employeeData.emergency_contact_name, ''),
          emergencyContactRelation: getValue(employeeData.emergencyContactRelation || employeeData.emergency_contact_relation, ''),
          emergencyContactNumber: getValue(employeeData.emergencyContactNumber || employeeData.emergency_contact_number, ''),

          department: getValue(employeeData.department, ''),
          position: getValue(employeeData.position || employeeData.designation, ''),
          workLocation: getValue(employeeData.workLocation || employeeData.work_location, ''),
          employmentType: getValue(employeeData.employmentType || employeeData.employment_type, 'Full-time'),
          joiningDate: getValue(employeeData.joiningDate || employeeData.joining_date, ''),
          status: getValue(employeeData.status, 'Active'),
          weekly_off_days: employeeData.weekly_off_days || ['Sunday'],

          basicSalary: getValue(employeeData.basicSalary || employeeData.basic_salary, ''),
          currentSalary: getValue(employeeData.currentSalary || employeeData.current_salary, ''),
          previousCompany: getValue(employeeData.previousCompany || employeeData.previous_company, ''),
          previousSalary: getValue(employeeData.previousSalary || employeeData.previous_salary, ''),

          bankName: getValue(employeeData.bankName || employeeData.bank_name, ''),
          accountNumber: getValue(employeeData.accountNumber || employeeData.account_number, ''),
          ifscCode: getValue(employeeData.ifscCode || employeeData.ifsc_code, ''),

          // Documents
          bankPassbook: getValue(employeeData.bankPassbook || employeeData.bank_passbook, null),
          aadharCard: getValue(employeeData.aadharCard || employeeData.aadhar_card, null),
          panCard: getValue(employeeData.panCard || employeeData.pan_card, null),
          passportPhoto: getValue(employeeData.passportPhoto || employeeData.passport_photo, null),
          tenthMarksheet: getValue(employeeData.tenthMarksheet || employeeData.tenth_marksheet, null),
          twelfthMarksheet: getValue(employeeData.twelfthMarksheet || employeeData.twelfth_marksheet, null),
          graduationMarksheet: getValue(employeeData.graduationMarksheet || employeeData.graduation_marksheet, null),
          resume: getValue(employeeData.resume, null),
          previousSalarySlip: getValue(employeeData.previousSalarySlip || employeeData.previous_salary_slip, null),
          previousExperienceCertificate: getValue(employeeData.previousExperienceCertificate || employeeData.previous_experience_certificate, null),
          currentExperienceLetter: getValue(employeeData.currentExperienceLetter || employeeData.current_experience_letter, null),
          offerLetter: getValue(employeeData.offerLetter || employeeData.offer_letter, null),
          appointmentLetter: getValue(employeeData.appointmentLetter || employeeData.appointment_letter, null),
          postGraduationMarksheet: getValue(employeeData.postGraduationMarksheet || employeeData.post_graduation_marksheet, null),
        };

        setFormData(mapped);

      } catch (error) {
        ('❌ Error fetching employee data:', error);
        alert(`Failed to load employee data: ${error.message}`);
        navigate('/employees');
      } finally {
        setFetchingData(false);
      }
    };

    loadEmployeeData();
  }, [id, navigate]);
  // ── Handlers ──────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFileUpload = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) { alert('PNG, JPG, JPEG, or PDF only'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('Max 5MB'); return; }
    setFormData(prev => ({ ...prev, [fieldName]: file }));
  };

  const removeFile = (fieldName) => {
    setFormData(prev => ({ ...prev, [fieldName]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = 'Full name is required';
    if (!formData.email?.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.employeeId?.trim()) newErrors.employeeId = 'Employee ID is required';
    if (!formData.phone?.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.department) newErrors.department = 'Department is required';
    if (!formData.position?.trim()) newErrors.position = 'Position is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Submit ─────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { alert('Please fix the errors before submitting'); return; }
    setLoading(true);

    try {
      const fd = new FormData();

      const textMap = {
        phone: formData.phone,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        bloodGroup: formData.bloodGroup,
        maritalStatus: formData.maritalStatus,
        nationality: formData.nationality,
        fatherName: formData.fatherName,
        fatherContact: formData.fatherContact,
        motherName: formData.motherName,
        motherContact: formData.motherContact,
        spouseName: formData.spouseName,
        spouseContact: formData.spouseContact,
        currentAddress: formData.address, // Map 'address' to 'currentAddress'
        permanentAddress: formData.permanentAddress,
        emergencyContactName: formData.emergencyContactName,
        emergencyContactRelation: formData.emergencyContactRelation,
        emergencyContactNumber: formData.emergencyContactNumber,
        department: formData.department,
        position: formData.position || '',
        workLocation: formData.workLocation,
        employmentType: formData.employmentType,
        joiningDate: formData.joiningDate,
        status: formData.status,
        previousCompany: formData.previousCompany,
        bankName: formData.bankName,
        accountNumber: formData.accountNumber,
        ifscCode: formData.ifscCode,
        weekly_off_days: JSON.stringify(formData.weekly_off_days),
      };

      Object.entries(textMap).forEach(([key, val]) => {
        if (val !== null && val !== undefined && val !== '') {
          fd.append(key, val);
        }
      });

      ['basicSalary', 'currentSalary', 'previousSalary'].forEach(key => {
        const val = formData[key];
        if (val !== '' && val !== null && val !== undefined && !isNaN(Number(val))) {
          fd.append(key, val);
        }
      });

      ALL_DOC_FIELDS.forEach(field => {
        if (formData[field] instanceof File) {
          fd.append(field, formData[field]);
        }
      });

      for (const [k, v] of fd.entries()) console.log(' ', k, ':', v);

      const response = await api.patch(`/api/employees/${formData.id}/`, fd);

      refreshData();
      alert('Employee updated successfully!');
      sessionStorage.removeItem('editEmployee');
      navigate(`/employees/${formData.id}`);

    } catch (error) {
      ('❌ PATCH failed:', error);
      let msg = 'Update failed:\n';
      if (error.response?.data && typeof error.response.data === 'object') {
        Object.entries(error.response.data).forEach(([field, messages]) => {
          msg += `\n• ${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`;
        });
      } else {
        msg = error.message || 'Unknown error';
      }
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Information', icon: FiUser },
    { id: 'family', label: 'Family Details', icon: FiUsers },
    { id: 'address', label: 'Address', icon: FiHome },
    { id: 'employment', label: 'Employment', icon: FiBriefcase },
    { id: 'salary', label: 'Salary & Bank', icon: FiDollarSign },
    { id: 'documents', label: 'Documents', icon: FiFile },
  ];

  // ⏳ Show loading spinner while fetching from backend
  if (fetchingData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading employee data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-employee-page animate-fade-in">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 -mt-6 -mx-4 md:-mx-6 px-4 md:px-6 py-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/employees')} className="p-2 hover:bg-gray-100 rounded-lg transition">
              <FiArrowLeft size={24} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-800">Edit Employee</h1>
              <p className="text-sm text-gray-500 mt-1">Update information for {formData.name || 'Employee'}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => navigate('/employees')}
              className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition">
              Cancel
            </button>
            <button onClick={handleSubmit} disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition shadow-md disabled:opacity-50">
              <FiSave size={18} />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Employee card */}
      <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-2xl p-4 md:p-6 mb-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold">
              {formData.name?.charAt(0) || 'E'}
            </div>
            <div>
              <p className="text-sm opacity-90">Employee ID</p>
              <p className="text-xl font-bold">{formData.employeeId || 'Not set'}</p>
              <p className="text-sm opacity-90 mt-1">{formData.department || 'Department not set'}</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div>
              <p className="text-sm opacity-90">Status</p>
              <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${formData.status === 'Active' ? 'bg-green-400 text-green-900' : 'bg-yellow-400 text-yellow-900'}`}>
                {formData.status || 'Active'}
              </span>
            </div>
            <div>
              <p className="text-sm opacity-90">Joining Date</p>
              <p className="font-medium">{formData.joiningDate || 'Not set'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition border-b-2 ${activeTab === tab.id
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Tab */}
        {activeTab === 'basic' && (
          <div className="form-section">
            <h3 className="section-title">📋 Basic Information</h3>
            <div className="form-grid grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Full Name" name="name" required readOnly value={formData.name} error={errors.name} onChange={handleChange} />
              <InputField label="Employee ID" name="employeeId" required readOnly value={formData.employeeId} error={errors.employeeId} onChange={handleChange} />
              <InputField label="Email Address" name="email" required readOnly value={formData.email} error={errors.email} onChange={handleChange} />
              <InputField label="Phone Number" name="phone" required type="tel" placeholder="+91 98765 43210" value={formData.phone} error={errors.phone} onChange={handleChange} />
              <InputField label="Date of Birth" name="dateOfBirth" type="date" value={formData.dateOfBirth} onChange={handleChange} />
              <SelectField label="Gender" name="gender"
                options={[{ value: 'Male', label: 'Male' }, { value: 'Female', label: 'Female' }, { value: 'Other', label: 'Other' }]}
                value={formData.gender} onChange={handleChange} />
              <SelectField label="Blood Group" name="bloodGroup"
                options={[
                  { value: '', label: 'Select' }, { value: 'A+', label: 'A+' }, { value: 'A-', label: 'A-' },
                  { value: 'B+', label: 'B+' }, { value: 'B-', label: 'B-' }, { value: 'O+', label: 'O+' },
                  { value: 'O-', label: 'O-' }, { value: 'AB+', label: 'AB+' }, { value: 'AB-', label: 'AB-' },
                ]}
                value={formData.bloodGroup} onChange={handleChange} />
              <SelectField label="Marital Status" name="maritalStatus"
                options={[
                  { value: 'Single', label: 'Single' }, { value: 'Married', label: 'Married' },
                  { value: 'Divorced', label: 'Divorced' }, { value: 'Widowed', label: 'Widowed' },
                ]}
                value={formData.maritalStatus} onChange={handleChange} />
              <InputField label="Nationality" name="nationality" placeholder="Indian" value={formData.nationality} onChange={handleChange} />
            </div>
          </div>
        )}

        {/* Family Tab */}
        {activeTab === 'family' && (
          <div className="form-section">
            <h3 className="section-title">👨‍👩‍👧‍👦 Family Information</h3>
            <div className="form-grid grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Father's Name" name="fatherName" placeholder="Enter father's name" value={formData.fatherName} onChange={handleChange} />
              <InputField label="Father's Contact" name="fatherContact" placeholder="+91 98765 43210" value={formData.fatherContact} onChange={handleChange} />
              <InputField label="Mother's Name" name="motherName" placeholder="Enter mother's name" value={formData.motherName} onChange={handleChange} />
              <InputField label="Mother's Contact" name="motherContact" placeholder="+91 98765 43210" value={formData.motherContact} onChange={handleChange} />
              <InputField label="Spouse Name" name="spouseName" placeholder="Enter spouse name" value={formData.spouseName} onChange={handleChange} />
              <InputField label="Spouse Contact" name="spouseContact" placeholder="+91 98765 43210" value={formData.spouseContact} onChange={handleChange} />
            </div>
          </div>
        )}

        {/* Address Tab */}
        {activeTab === 'address' && (
          <div className="form-section">
            <h3 className="section-title">📍 Address Information</h3>
            <div className="space-y-4">
              <TextAreaField label="Current Address" name="address" rows={3} placeholder="House No., Street, City, State, PIN" value={formData.address} onChange={handleChange} />
              <TextAreaField label="Permanent Address" name="permanentAddress" rows={3} placeholder="House No., Street, City, State, PIN" value={formData.permanentAddress} onChange={handleChange} />
              <div className="form-grid grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField label="Emergency Contact Name" name="emergencyContactName" placeholder="Contact person name" value={formData.emergencyContactName} onChange={handleChange} />
                <InputField label="Relationship" name="emergencyContactRelation" placeholder="Father, Mother, Spouse" value={formData.emergencyContactRelation} onChange={handleChange} />
                <InputField label="Emergency Contact Number" name="emergencyContactNumber" placeholder="+91 98765 43210" value={formData.emergencyContactNumber} onChange={handleChange} />
              </div>
            </div>
          </div>
        )}

        {/* Employment Tab */}
        {activeTab === 'employment' && (
          <div className="form-section">
            <h3 className="section-title">💼 Employment Information</h3>
            <div className="form-grid grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField label="Department" name="department"
                options={[
                  { value: 'Engineering', label: 'Engineering' }, { value: 'HR', label: 'Human Resources' },
                  { value: 'Finance', label: 'Finance' }, { value: 'Marketing', label: 'Marketing' },
                  { value: 'Sales', label: 'Sales' }, { value: 'Operations', label: 'Operations' },
                ]}
                value={formData.department} onChange={handleChange} />
              <InputField label="Position / Designation" name="position" required placeholder="Senior Manager" value={formData.position} error={errors.position} onChange={handleChange} />
              <InputField label="Work Location" name="workLocation" placeholder="City, Office location" value={formData.workLocation} onChange={handleChange} />
              <SelectField label="Employment Type" name="employmentType"
                options={[
                  { value: 'Full-time', label: 'Full-time' }, { value: 'Part-time', label: 'Part-time' },
                  { value: 'Contract', label: 'Contract' }, { value: 'Internship', label: 'Internship' },
                ]}
                value={formData.employmentType} onChange={handleChange} />
              <InputField label="Joining Date" name="joiningDate" type="date" value={formData.joiningDate} onChange={handleChange} />
              <SelectField label="Status" name="status"
                options={[
                  { value: 'Active', label: 'Active' }, { value: 'On Leave', label: 'On Leave' },
                  { value: 'Resigned', label: 'Resigned' }, { value: 'Terminated', label: 'Terminated' },
                ]}
                value={formData.status} onChange={handleChange} />
              <InputField label="Weekly Off Days" name="weekly_off_days"
                placeholder="Sunday, Saturday"
                value={Array.isArray(formData.weekly_off_days) ? formData.weekly_off_days.join(', ') : ''}
                onChange={(e) => {
                  const days = e.target.value.split(',').map(d => d.trim()).filter(d => d);
                  setFormData(prev => ({ ...prev, weekly_off_days: days.length ? days : ['Sunday'] }));
                }} />
            </div>
          </div>
        )}

        {/* Salary Tab */}
        {activeTab === 'salary' && (
          <div className="form-section">
            <h3 className="section-title">💰 Salary & Bank Information</h3>
            <div className="form-grid grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Basic Salary (₹)" name="basicSalary" type="number" placeholder="50000" value={formData.basicSalary} onChange={handleChange} />
              <InputField label="Current Salary (₹)" name="currentSalary" type="number" placeholder="50000" value={formData.currentSalary} onChange={handleChange} />
              <InputField label="Previous Company" name="previousCompany" placeholder="Previous employer" value={formData.previousCompany} onChange={handleChange} />
              <InputField label="Previous Salary (₹)" name="previousSalary" type="number" placeholder="45000" value={formData.previousSalary} onChange={handleChange} />
            </div>
            <div className="mt-6">
              <h4 className="text-md font-semibold text-gray-800 mb-4">🏦 Bank Details</h4>
              <div className="form-grid grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField label="Bank Name" name="bankName" placeholder="Bank name" value={formData.bankName} onChange={handleChange} />
                <InputField label="Account Number" name="accountNumber" placeholder="Account number" value={formData.accountNumber} onChange={handleChange} />
                <InputField label="IFSC Code" name="ifscCode" placeholder="IFSC code" value={formData.ifscCode} onChange={handleChange} />
              </div>
              <div className="mt-4">
                <FileUploadField label="Bank Passbook / Cancelled Cheque" fieldName="bankPassbook"
                  value={formData.bankPassbook} onUpload={handleFileUpload} onRemove={removeFile} />
              </div>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="form-section">


            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-800 mb-4">Personal Documents</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FileUploadField label="Aadhar Card" fieldName="aadharCard" value={formData.aadharCard} onUpload={handleFileUpload} onRemove={removeFile} />
                <FileUploadField label="PAN Card" fieldName="panCard" value={formData.panCard} onUpload={handleFileUpload} onRemove={removeFile} />
                <FileUploadField label="Passport Size Photo" fieldName="passportPhoto" accept=".jpg,.jpeg,.png"
                  value={formData.passportPhoto} onUpload={handleFileUpload} onRemove={removeFile} />
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-800 mb-4">Employment Documents</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FileUploadField label="Offer Letter" fieldName="offerLetter" value={formData.offerLetter} onUpload={handleFileUpload} onRemove={removeFile} />
                <FileUploadField label="Appointment Letter" fieldName="appointmentLetter" value={formData.appointmentLetter} onUpload={handleFileUpload} onRemove={removeFile} />
                <FileUploadField label="Previous Salary Slip" fieldName="previousSalarySlip" value={formData.previousSalarySlip} onUpload={handleFileUpload} onRemove={removeFile} />
                <FileUploadField label="Experience Certificate" fieldName="previousExperienceCertificate" value={formData.previousExperienceCertificate} onUpload={handleFileUpload} onRemove={removeFile} />
                <FileUploadField label="Current Experience Letter" fieldName="currentExperienceLetter" value={formData.currentExperienceLetter} onUpload={handleFileUpload} onRemove={removeFile} />
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-800 mb-4">Educational Documents</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FileUploadField label="10th Marksheet" fieldName="tenthMarksheet" value={formData.tenthMarksheet} onUpload={handleFileUpload} onRemove={removeFile} />
                <FileUploadField label="12th Marksheet" fieldName="twelfthMarksheet" value={formData.twelfthMarksheet} onUpload={handleFileUpload} onRemove={removeFile} />
                <FileUploadField label="Graduation Marksheet" fieldName="graduationMarksheet" value={formData.graduationMarksheet} onUpload={handleFileUpload} onRemove={removeFile} />
                <FileUploadField label="Post Graduation" fieldName="postGraduationMarksheet" value={formData.postGraduationMarksheet} onUpload={handleFileUpload} onRemove={removeFile} />
              </div>
            </div>

            <div>
              <h4 className="text-md font-semibold text-gray-800 mb-4">Resume</h4>
              <FileUploadField label="Upload Resume" fieldName="resume" value={formData.resume} onUpload={handleFileUpload} onRemove={removeFile} />
            </div>
          </div>
        )}

        {/* Bottom Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 -mx-4 md:-mx-6 px-4 md:px-6 py-4 mt-6">
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => navigate('/employees')}
              className="px-6 py-2.5 border border-gray-300 rounded-xl hover:bg-gray-100 transition">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition disabled:opacity-50 flex items-center gap-2">
              <FiSave size={18} />
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditEmployee;